import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowPathIcon, ShieldExclamationIcon } from "@heroicons/react/24/outline";
import CustomDialog from "../../../components/modals/CustomDialog/CustomDialog";
import { Button } from "../../../components/forms/Buttons/Button";
import { getDocumentDownloads } from "../../../services/bookletPrintService";
import { getApiErrorMessage } from "../../../utils/apiError";
import { formatDateTime } from "../../../utils/formatDate";
import { Spinner } from "../../Exams/TestBuilder/ui";
import { personName } from "./printText";
import { dialogFooterClass, phoneButtonClass } from "./ui";

/**
 * Asks before something that is written to the log (a key opened or downloaded). The focus starts on "Cancel".
 * `onConfirm` closes it and acts.
 */
export function ConfirmDialog({ isOpen, title, children, confirmText, ConfirmIcon, onConfirm, onCancel }) {
  const [t] = useTranslation();
  return (
    <CustomDialog isOpen={isOpen} closeModal={onCancel} headerText={title} maxWidth="max-w-md">
      <div className="mt-3 flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600 ring-1 ring-inset ring-amber-600/20">
          <ShieldExclamationIcon className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 space-y-2 pt-0.5 text-sm text-slate-600">{children}</div>
      </div>
      <div className={dialogFooterClass}>
        <Button type="button" text={t("buttons.cancel")} color="secondary-outline" onClick={onCancel} className={phoneButtonClass} />
        <Button type="button" text={confirmText} Icon={ConfirmIcon} color="primary" onClick={onConfirm} className={phoneButtonClass} />
      </div>
    </CustomDialog>
  );
}

/**
 * Who downloaded a document, when and from where (GET booklet-documents/<uuid>/downloads/), read each time it
 * opens. `document` stays set while it closes; `label` names it ("Variant 9104 · Answer key").
 */
export function DownloadLogDialog({ isOpen, document, label, onClose }) {
  const [t, i18n] = useTranslation();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ loading: false, error: null, entries: [] });

  useEffect(() => {
    if (!isOpen || !document) return undefined;
    const controller = new AbortController();
    setState({ loading: true, error: null, entries: [] });
    getDocumentDownloads(document.uuid, { signal: controller.signal })
      .then((entries) => setState({ loading: false, error: null, entries: Array.isArray(entries) ? entries : [] }))
      .catch((err) => {
        if (!controller.signal.aborted) setState({ loading: false, error: err, entries: [] });
      });
    return () => controller.abort();
  }, [isOpen, document, attempt]);

  const { loading, error, entries } = state;
  return (
    <CustomDialog isOpen={isOpen} closeModal={onClose} headerText={t("bts.print.log.title")} maxWidth="max-w-2xl">
      {label && <p className="text-sm text-slate-500">{label}</p>}
      <div className="mt-4" aria-live="polite" aria-busy={loading}>
        {loading ? (
          <p className="flex items-center gap-2 py-6 text-sm text-slate-500" role="status">
            <Spinner />
            {t("bts.print.log.loading")}
          </p>
        ) : error ? (
          <div className="flex flex-col items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="alert">
            <p>{getApiErrorMessage(t, error, "bts.print.log.error")}</p>
            <Button
              type="button"
              text={t("bts.print.retry")}
              Icon={ArrowPathIcon}
              color="secondary-outline"
              onClick={() => setAttempt((n) => n + 1)}
              className={phoneButtonClass}
            />
          </div>
        ) : entries.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">
            {t("bts.print.log.empty")}
          </p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <div
              className="hidden gap-4 border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-medium text-slate-500 sm:grid sm:grid-cols-[10rem_minmax(0,1fr)_9rem]"
              aria-hidden="true"
            >
              <span>{t("bts.print.log.when")}</span>
              <span>{t("bts.print.log.who")}</span>
              <span>{t("bts.print.log.ip")}</span>
            </div>
            <ol className="max-h-[60vh] divide-y divide-slate-100 overflow-y-auto">
              {entries.map((entry, index) => {
                const name = personName(entry.user);
                const username = entry.user?.username;
                return (
                  <li
                    key={`${entry.created}-${index}`}
                    className="grid gap-x-4 gap-y-0.5 px-4 py-2.5 text-sm sm:grid-cols-[10rem_minmax(0,1fr)_9rem] sm:items-center"
                  >
                    <span className="font-medium tabular-nums text-slate-900 sm:font-normal sm:text-slate-700">
                      <span className="sr-only">{t("bts.print.log.when")}: </span>
                      {formatDateTime(entry.created, i18n.language)}
                    </span>
                    <span className="min-w-0 break-words text-slate-700">
                      <span className="sr-only">{t("bts.print.log.who")}: </span>
                      {entry.user ? (
                        <>
                          {name}
                          {username && username !== name && <span className="text-slate-500"> · {username}</span>}
                        </>
                      ) : (
                        <span className="text-slate-500">{t("bts.print.log.unknown_user")}</span>
                      )}
                    </span>
                    <span className="font-mono text-xs text-slate-500 sm:text-sm">
                      <span className="sm:sr-only">{t("bts.print.log.ip")}: </span>
                      {/* An IPv6 address (up to 64 characters) wraps inside its column. */}
                      <span className="break-all">{entry.ip || "—"}</span>
                    </span>
                  </li>
                );
              })}
            </ol>
          </div>
        )}
      </div>
      <div className={dialogFooterClass}>
        <Button type="button" text={t("buttons.close")} color="secondary-outline" onClick={onClose} className={phoneButtonClass} />
      </div>
    </CustomDialog>
  );
}
